/**
 * Implementation primitives for the per-platform **coverage** polygon layer
 * (PR5b inc4; REQ-UI-070; ADR-032 §4.2) — a thin per-kind binding over the
 * shared geometry-polygon mechanism in :file:`geometry-polygon-layer.ts`.
 * Sibling of :file:`fov-layer-impl.ts` / :file:`obscuration-layer-impl.ts`;
 * split from :file:`coverage-layer.tsx` per ``react-refresh/only-export-components``.
 *
 * Unlike FoV (at most one ring per platform), a platform's coverage is a
 * **list** of rings (:type:`StatusGeometry.coverage` — ``number[][][]``), so
 * the ring extractor returns that list directly (``g => g.coverage``) and a
 * single platform contributes **one ``Polygon`` feature per ring** (each
 * tagged with its ``ring_index``). The shared mechanism then renders ALL
 * platforms' coverage rings in one source as a faint gold fill (``--geom-
 * coverage`` at low opacity) plus a more-saturated outline
 * (``--geom-coverage-stroke``) so the footprint stays operator-visible once
 * the fill drops to a wash (CLAUDE.md §8.1). Both paints resolve through the
 * inc1 design-system readers so the theme flip carries through on the next
 * paint (REQ-UI-040).
 *
 * The fill + line layers are inserted **below** the snapshot marker layer
 * (handled by the shared :func:`attachGeometryPolygonLayer`) so the platform
 * markers always paint on top of the geometry wash (per §8.1).
 *
 * Cross-references:
 *   - REQ-UI-070 (SAPIENT map geometry — coverage layer) / REQ-UI-040 /
 *     REQ-UI-046.
 *   - ADR-032 §4.2 (Status geometry data model — coverage ring list).
 *   - CLAUDE.md §4.4 (no duplicated logic) / §8.1 (markers above the wash).
 *   - map/geometry-polygon-layer.ts (the shared mechanism this delegates to).
 *   - map/marker-styling.ts (getGeomColor / getGeomStrokeColor).
 */
import type { Map as MapLibreMap } from "maplibre-gl";
import { getGeomColor, getGeomStrokeColor } from "@/map/marker-styling";
import type { StatusGeometry } from "@/map/sse-types";
import {
  type GeometryEntry,
  type GeometryFeatureProps,
  type RingExtractor,
  attachGeometryPolygonLayer,
  detachGeometryPolygonLayer,
  ringsToPolygonFeatures,
} from "@/map/geometry-polygon-layer";

/** MapLibre GeoJSON source ID for the coverage polygons. */
export const COVERAGE_SOURCE_ID = "meridian-coverage-source";
/** MapLibre fill-layer ID for the coverage wash. */
export const COVERAGE_FILL_LAYER_ID = "meridian-coverage-fill-layer";
/** MapLibre line-layer ID for the coverage outline. */
export const COVERAGE_LINE_LAYER_ID = "meridian-coverage-line-layer";

/**
 * Fill opacity for the coverage wash — faint so the basemap + markers (and
 * the obscuration / FoV washes that may stack with it) read through it; the
 * saturated outline carries the shape. Within the ~0.12–0.2 band documented
 * on :func:`getGeomColor`.
 */
export const COVERAGE_FILL_OPACITY = 0.15;
/** Outline width in screen px — thin but legible against the wash. */
export const COVERAGE_LINE_WIDTH_PX = 1.5;

/**
 * A platform's coverage entry as the feature builder consumes it. Alias of
 * the shared :type:`GeometryEntry`.
 */
export type CoverageEntry = GeometryEntry;

/**
 * Pull the coverage ring **list** out of a platform's geometry. Returns
 * ``geometry.coverage`` directly (``number[][][]``); the shared builder
 * emits one feature per drawable ring (skipping null / < 3-point rings) and
 * tags each with its ``ring_index``.
 */
const extractCoverageRings: RingExtractor = (geometry: StatusGeometry) => geometry.coverage;

/**
 * Build the GeoJSON source payload — one closed, antimeridian-unwrapped
 * ``Polygon`` feature **per coverage ring** across all platforms (a platform
 * with two coverage rings yields two features, ``ring_index`` 0 and 1).
 * Delegates to the shared :func:`ringsToPolygonFeatures` with the coverage
 * ring extractor (CLAUDE.md §4.4); null / too-short rings are skipped.
 *
 * Properties carry ``producer_id`` + ``ring_index``. Exported for unit
 * testability.
 */
export function coverageToFeatureCollection(
  entries: readonly CoverageEntry[],
): GeoJSON.FeatureCollection<GeoJSON.Polygon, GeometryFeatureProps> {
  return ringsToPolygonFeatures(entries, extractCoverageRings);
}

/**
 * Register the coverage source + fill layer + line layer (idempotent: later
 * calls ``setData`` + re-apply the theme-resolved paint colours), painted
 * **below** the snapshot marker layer (CLAUDE.md §8.1). Delegates to the
 * shared :func:`attachGeometryPolygonLayer` with the coverage IDs + paints +
 * theme-resolved colours.
 *
 * Exported for unit testability. The :class:`CoverageLayer` component (in
 * :file:`coverage-layer.tsx`) owns the React-effect lifecycle + the
 * visibility/zoom gate (via the shared :func:`useGeometryLayerEffect`).
 */
export function attachCoverageLayer(map: MapLibreMap, entries: readonly CoverageEntry[]): void {
  attachGeometryPolygonLayer(map, {
    sourceId: COVERAGE_SOURCE_ID,
    fillLayerId: COVERAGE_FILL_LAYER_ID,
    lineLayerId: COVERAGE_LINE_LAYER_ID,
    features: coverageToFeatureCollection(entries),
    fillColor: getGeomColor("coverage"),
    strokeColor: getGeomStrokeColor("coverage"),
    fillOpacity: COVERAGE_FILL_OPACITY,
    lineWidth: COVERAGE_LINE_WIDTH_PX,
  });
}

/**
 * Tear down the coverage fill + line layers and source. Delegates to the
 * shared :func:`detachGeometryPolygonLayer`. No-op when nothing is
 * registered.
 */
export function detachCoverageLayer(map: MapLibreMap): void {
  detachGeometryPolygonLayer(map, {
    sourceId: COVERAGE_SOURCE_ID,
    fillLayerId: COVERAGE_FILL_LAYER_ID,
    lineLayerId: COVERAGE_LINE_LAYER_ID,
  });
}
